import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { HitsterEvent, EventRound, EventSong, HitsterCategoryCustom } from '../types';
import PhysicalBingoCard from '../components/BingoCard';
import './MusicBingo.css';

const API_BASE = process.env.REACT_APP_API_URL || '';
const THEME_NIGHT_ROUND_ID = 'theme-night';

const getEventType = (event: HitsterEvent | null | undefined): 'rounds' | 'theme-night' => {
  if (!event || event.eventType === 'rounds' || event.eventType === 'theme-night') {
    return event?.eventType || 'rounds';
  }
  return 'rounds';
};

interface BingoCardData {
  id: string;
  cells: (EventSong | HitsterCategoryCell | null)[][]; // 5x5 grid, center can be null (free space)
}

type BingoMode = 'music' | 'hitster';
type HitsterDifficulty = 'beginner' | 'expert' | 'mixed' | 'empty';

interface HitsterCategory {
  id: string;
  label: string;
  description: string;
  color: string;
  icon: string;
}

interface HitsterCategoryCell {
  categoryId: string;
  label: string;
  description: string;
}

interface AnswerKeyEntry {
  songId: string;
  categoryId: string | null;
  playedAt: number;
}

const HITSTER_BEGINNER_CATEGORIES: HitsterCategory[] = [
  {
    id: 'group-or-solo',
    label: 'Groep of soloartiest',
    description: 'Schrijf op of het nummer van een soloartiest of muziekgroep is.',
    color: '#9adf7f',
    icon: '/hitster-bingo-group.webp'
  },
  {
    id: 'before-2000',
    label: 'Voor 2000?',
    description: 'Schrijf Ja als het nummer voor 2000 is uitgebracht, anders Nee.',
    color: '#d8b5ff',
    icon: '/hitster-bingo-2000.webp'
  },
  {
    id: 'within-4-years',
    label: '4 jaar ervoor/erna',
    description: 'Schrijf het jaar op. Je scoort als je antwoord binnen 4 jaar ligt.',
    color: '#ffe68a',
    icon: '/hitster-bingo-4.webp'
  },
  {
    id: 'decade',
    label: 'Decennium',
    description: 'Noem het juiste decennium (bijv. jaren 80).',
    color: '#ff8fd6',
    icon: '/hitster-bingo-decada.webp'
  },
  {
    id: 'within-2-years',
    label: '2 jaar ervoor/erna',
    description: 'Schrijf het jaar op. Je scoort als je antwoord binnen 2 jaar ligt.',
    color: '#8ad9ff',
    icon: '/hitster-bingo-2.webp'
  }
];

const HITSTER_EXPERT_CATEGORIES: HitsterCategory[] = [
  {
    id: 'song-title',
    label: 'Songtitel',
    description: 'Schrijf de titel van het nummer op.',
    color: '#9adf7f',
    icon: '/hitster-bingo-b-songtitle.webp'
  },
  {
    id: 'exact-year',
    label: 'Exacte jaar',
    description: 'Schrijf het jaar op waarin het nummer is uitgebracht.',
    color: '#d8b5ff',
    icon: '/hitster-bingo-b-exactyear.webp'
  },
  {
    id: 'artist-name',
    label: 'Artiest/groep',
    description: 'Schrijf de naam van groep of zanger(es) op.',
    color: '#ffe68a',
    icon: '/hitster-bingo-b-artist.webp'
  },
  {
    id: 'decade',
    label: 'Decennium',
    description: 'Noem het juiste decennium (bijv. jaren 90).',
    color: '#ff8fd6',
    icon: '/hitster-bingo-b-decade.webp'
  },
  {
    id: 'within-3-years',
    label: '3 jaar ervoor/erna',
    description: 'Schrijf het jaar op. Je scoort als je antwoord binnen 3 jaar ligt.',
    color: '#8ad9ff',
    icon: '/hitster-bingo-b-3years.webp'
  }
];

const isImageIcon = (icon: string): boolean => /\.(png|jpe?g|webp|svg)$/i.test(icon);

// Fisher-Yates shuffle
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Generate a unique bingo card
const generateBingoCard = (songs: EventSong[], cardId: string, useFreeSpace: boolean): BingoCardData => {
  const shuffledSongs = shuffleArray(songs);
  const cells: (EventSong | null)[][] = [];
  
  let songIndex = 0;
  for (let row = 0; row < 5; row++) {
    const rowCells: (EventSong | null)[] = [];
    for (let col = 0; col < 5; col++) {
      // Center cell (2,2) can be free space
      if (useFreeSpace && row === 2 && col === 2) {
        rowCells.push(null);
      } else {
        rowCells.push(shuffledSongs[songIndex % shuffledSongs.length]);
        songIndex++;
      }
    }
    cells.push(rowCells);
  }
  
  return { id: cardId, cells };
};

const getHitsterCategories = (difficulty: HitsterDifficulty, customCategories?: HitsterCategory[]): HitsterCategory[] => {
  if (difficulty === 'empty' && customCategories) {
    return customCategories;
  }
  if (difficulty === 'beginner') return HITSTER_BEGINNER_CATEGORIES;
  if (difficulty === 'expert') return HITSTER_EXPERT_CATEGORIES;

  return [
    ...HITSTER_BEGINNER_CATEGORIES.map((category) => ({
      ...category,
      id: `a-${category.id}`
    })),
    ...HITSTER_EXPERT_CATEGORIES.map((category) => ({
      ...category,
      id: `b-${category.id}`
    }))
  ];
};

// Color pattern generation for physical cards
const DEFAULT_PATTERN: string[][] = [
  ['c-cyan', 'c-yellow', 'c-green', 'c-purple', 'c-pink'],
  ['c-yellow', 'c-pink', 'c-cyan', 'c-yellow', 'c-purple'],
  ['c-green', 'c-purple', 'c-green', 'c-yellow', 'c-pink'],
  ['c-pink', 'c-cyan', 'c-cyan', 'c-pink', 'c-purple'],
  ['c-yellow', 'c-purple', 'c-green', 'c-cyan', 'c-green']
];

const makeGridPattern = (seedIndex: number): string[][] => {
  const base = DEFAULT_PATTERN.map(row => [...row]);
  const rowShift = seedIndex % 5;
  const colShift = (seedIndex * 2) % 5;
  const rows = base.map((_, i) => base[(i + rowShift) % 5]);
  const rotated = rows.map(row => row.map((_, j) => row[(j + colShift) % 5]));

  const mappingSets = [
    {},
    { 'c-cyan': 'c-green', 'c-green': 'c-cyan' },
    { 'c-pink': 'c-purple', 'c-purple': 'c-pink' },
    { 'c-yellow': 'c-cyan', 'c-cyan': 'c-yellow' },
    { 'c-green': 'c-pink', 'c-pink': 'c-green' }
  ];
  const map = mappingSets[seedIndex % mappingSets.length];

  return rotated.map(row => row.map(c => (map as any)[c] || c));
};

const getWriteText = (index: number, mode: string, names: string[]): string => {
  if (mode === 'blank') return '';
  if (mode === 'numbers') return `Kaart ${index + 1}`;
  return names[index] || `Kaart ${index + 1}`;
};

const generateHitsterBingoCard = (
  difficulty: HitsterDifficulty,
  cardId: string,
  useFreeSpace: boolean,
  customCategories?: HitsterCategory[]
): BingoCardData => {
  const categories = getHitsterCategories(difficulty, customCategories);
  const pool: HitsterCategoryCell[] = [];

  // Build a pool of 25 cells
  // For standard modes (5 categories): 5 categories x 5 occurrences each
  // For empty mode (10 categories): Each category once, fill rest with repeats
  const occurrences = categories.length <= 5 ? 5 : Math.ceil(25 / categories.length);
  
  for (let i = 0; i < occurrences; i++) {
    for (const category of categories) {
      if (pool.length < 25) {
        pool.push({
          categoryId: category.id,
          label: category.label,
          description: category.description
        });
      }
    }
  }

  const shuffledPool = shuffleArray(pool.slice(0, 25));
  const cells: (EventSong | HitsterCategoryCell | null)[][] = [];
  let cellIndex = 0;

  for (let row = 0; row < 5; row++) {
    const rowCells: (EventSong | HitsterCategoryCell | null)[] = [];
    for (let col = 0; col < 5; col++) {
      if (useFreeSpace && row === 2 && col === 2) {
        rowCells.push(null);
      } else {
        rowCells.push(shuffledPool[cellIndex]);
        cellIndex++;
      }
    }
    cells.push(rowCells);
  }

  return { id: cardId, cells };
};

const MusicBingo: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { eventId, roundId } = useParams<{ eventId: string; roundId: string }>();
  const [searchParams] = useSearchParams();
  const requestedMode = searchParams.get('mode');
  const requestedFocus = searchParams.get('focus');
  const isAnswersPortal = location.pathname.startsWith('/hitster-antwoorden');
  const pageTitle = isAnswersPortal ? '🧾 Hitster Antwoorden & Teams' : '🎵 Muziekbingo - Hitster Bingo';
  const getRouteForRound = useCallback((targetRoundId: string) => {
    if (!eventId || !targetRoundId) {
      return '/spotify-dashboard';
    }

    if (isAnswersPortal) {
      return `/hitster-antwoorden/${eventId}/${targetRoundId}`;
    }

    return `/music-bingo/${eventId}/${targetRoundId}`;
  }, [eventId, isAnswersPortal]);
  
  const [event, setEvent] = useState<HitsterEvent | null>(null);
  const [round, setRound] = useState<EventRound | null>(null);
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState<BingoCardData[]>([]);
  const [numCards, setNumCards] = useState(10);
  const [bingoMode, setBingoMode] = useState<BingoMode>(requestedMode === 'hitster' ? 'hitster' : 'music');
  const [hitsterDifficulty, setHitsterDifficulty] = useState<HitsterDifficulty>('beginner');
  const [customBingoCategories, setCustomBingoCategories] = useState<HitsterCategory[]>([]);
  const [showCategoryEditor, setShowCategoryEditor] = useState(false);
  const [editingCategoryIndex, setEditingCategoryIndex] = useState<number | null>(null);
  const [useFreeSpace, setUseFreeSpace] = useState(true);
  const [showArtist, setShowArtist] = useState(true);
  const [showTitle, setShowTitle] = useState(true);
  const [cardsPerPage, setCardsPerPage] = useState(2);
  const [beamAngle, setBeamAngle] = useState(-90);
  const [isSpinningDiscoball, setIsSpinningDiscoball] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [bingoPlayDurationSeconds, setBingoPlayDurationSeconds] = useState(25);
  const [displayPushStatus, setDisplayPushStatus] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle');
  const [revealPushStatus, setRevealPushStatus] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle');
  const [bingoCelebrationStatus, setBingoCelebrationStatus] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle');
  const [viewPhysicalCards, setViewPhysicalCards] = useState(false);
  // Hitster Bingo playlist tracking
  const [activeSongId, setActiveSongId] = useState<string | null>(null);
  const [playedSongIds, setPlayedSongIds] = useState<Set<string>>(new Set());
  // Physical card controls
  const [cardTitle, setCardTitle] = useState('HITSTER');
  const [cardWriteMode, setCardWriteMode] = useState<'blank' | 'names' | 'numbers'>('blank');
  const [cardNames, setCardNames] = useState('');
  // View layout mode for admin
  const [adminViewLayout, setAdminViewLayout] = useState<'stacked' | 'side-by-side'>('side-by-side');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  // Bingo scores tracking
  const [bingoScores, setBingoScores] = useState<Record<string, number>>({}); // teamId -> bingo count
  const [answerKeyEntries, setAnswerKeyEntries] = useState<AnswerKeyEntry[]>([]);
  const [aiExpectedAnswers, setAiExpectedAnswers] = useState<Record<string, string>>({});
  const [aiAnswerSources, setAiAnswerSources] = useState<Record<string, string>>({});
  const aiAnswerRequestsRef = useRef<Set<string>>(new Set());
  const effectiveRoundId = roundId || (getEventType(event) === 'theme-night' ? THEME_NIGHT_ROUND_ID : null);

  // Load event and round data
  useEffect(() => {
    const loadData = async () => {
      if (!eventId) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_BASE}/api/events/${eventId}`);
        if (response.ok) {
          const data = await response.json();
          setEvent(data);
          if (roundId) {
            const r = data.rounds.find((r: EventRound) => r.id === roundId);
            setRound(r || null);
            if (r) {
              setBingoPlayDurationSeconds(r.bingoPlayDurationSeconds ?? 25);
              // Load custom categories if they exist
              if (r.customBingoCategories && r.customBingoCategories.length > 0) {
                setCustomBingoCategories(r.customBingoCategories as HitsterCategory[]);
              } else {
                setCustomBingoCategories([]);
              }
            }
          } else if (getEventType(data) === 'theme-night') {
            setRound({
              id: THEME_NIGHT_ROUND_ID,
              name: 'Thema-avond',
              order: 1,
              songs: data.themeSongs || []
            });
            setBingoPlayDurationSeconds(25);
            setCustomBingoCategories([]);
          } else {
            setRound(null);
            setBingoPlayDurationSeconds(25);
            setCustomBingoCategories([]);
          }
        }
      } catch (err) {
        console.error('Failed to load event:', err);
      }
      setLoading(false);
    };
    loadData();
  }, [eventId, roundId]);

  useEffect(() => {
    setPlayedSongIds(new Set());
    setAnswerKeyEntries([]);
    setAiExpectedAnswers({});
    setAiAnswerSources({});
    aiAnswerRequestsRef.current.clear();
    setBingoScores({});
    setActiveSongId(null);
    setActiveCategoryId(null);
    setRevealPushStatus('idle');
    setDisplayPushStatus('idle');
    setBingoCelebrationStatus('idle');
  }, [effectiveRoundId]);

  useEffect(() => {
    if (requestedMode === 'hitster') {
      setBingoMode('hitster');
    }
  }, [requestedMode]);

  useEffect(() => {
    if (!round || bingoMode !== 'hitster') {
      return;
    }

    const targetId = requestedFocus === 'teams'
      ? 'bingo-scoreboard-panel'
      : requestedFocus === 'answers'
        ? 'answer-key-panel'
        : null;

    if (!targetId) {
      return;
    }

    const timeout = window.setTimeout(() => {
      const element = document.getElementById(targetId);
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);

    return () => window.clearTimeout(timeout);
  }, [requestedFocus, round, bingoMode]);

  useEffect(() => {
    const loadBingoScores = async () => {
      if (!eventId || !effectiveRoundId) {
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/bingo/scores/${eventId}/${effectiveRoundId}`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const scores = await response.json();
        setBingoScores(scores);
      } catch (error) {
        console.error('Failed to load bingo scores:', error);
      }
    };

    loadBingoScores();
  }, [eventId, effectiveRoundId]);

  // Load saved answer keys when round changes
  useEffect(() => {
    const loadAnswerKeys = async () => {
      if (!eventId || !effectiveRoundId) {
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/answer-keys/${eventId}/${effectiveRoundId}`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const entries = await response.json();
        setAnswerKeyEntries(entries);
      } catch (error) {
        console.error('Failed to load answer keys:', error);
      }
    };

    loadAnswerKeys();
  }, [eventId, effectiveRoundId]);

  // Auto-save answer keys whenever they change
  useEffect(() => {
    if (!eventId || !effectiveRoundId || answerKeyEntries.length === 0) {
      return;
    }

    const saveAnswerKeys = async () => {
      try {
        await fetch(`${API_BASE}/api/answer-keys/${eventId}/${effectiveRoundId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entries: answerKeyEntries })
        });
      } catch (error) {
        console.error('Failed to save answer keys:', error);
      }
    };

    // Use debounce to avoid saving too frequently
    const timeoutId = window.setTimeout(saveAnswerKeys, 500);
    return () => clearTimeout(timeoutId);
  }, [eventId, effectiveRoundId, answerKeyEntries]);

  // Generate cards
  const generateCards = useCallback(() => {
    const songs = round?.songs || [];
    if (bingoMode === 'music' && songs.length < 24) {
      alert(`Je hebt minimaal 24 nummers nodig voor bingokaartjes. Je hebt er nu ${songs.length}.`);
      return;
    }
    
    const newCards: BingoCardData[] = [];
    for (let i = 0; i < numCards; i++) {
      if (bingoMode === 'hitster') {
        newCards.push(generateHitsterBingoCard(hitsterDifficulty, `card-${i + 1}`, useFreeSpace, customBingoCategories));
      } else {
        newCards.push(generateBingoCard(songs, `card-${i + 1}`, useFreeSpace));
      }
    }
    setCards(newCards);
  }, [round, numCards, useFreeSpace, bingoMode, hitsterDifficulty, customBingoCategories]);

  const CUSTOM_COLORS = [
    '#9adf7f', // green
    '#d8b5ff', // purple
    '#ffe68a', // yellow
    '#ff8fd6', // pink
    '#8ad9ff'  // cyan
  ];

  const saveCustomCategories = useCallback(async () => {
    if (!eventId || !effectiveRoundId || !round) return;
    
    try {
      const updatedRound = {
        ...round,
        customBingoCategories: customBingoCategories
      };
      
      const response = await fetch(`${API_BASE}/api/events/${eventId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rounds: event?.rounds.map((r: EventRound) => 
            r.id === effectiveRoundId ? updatedRound : r
          )
        })
      });

      if (!response.ok) {
        console.error('Failed to save custom categories');
      }
    } catch (error) {
      console.error('Failed to save custom categories:', error);
    }
  }, [eventId, effectiveRoundId, round, event, customBingoCategories]);

  const addCustomCategory = () => {
    if (customBingoCategories.length >= 10) {
      alert('Maximaal 10 categorieën toegestaan');
      return;
    }
    
    const newCategory: HitsterCategory = {
      id: `custom-${Date.now()}`,
      label: 'Nieuwe categorie',
      description: 'Beschrijving',
      color: CUSTOM_COLORS[customBingoCategories.length % CUSTOM_COLORS.length],
      icon: '🎵'
    };
    
    const updated = [...customBingoCategories, newCategory];
    setCustomBingoCategories(updated);
  };

  const updateCustomCategory = (index: number, field: keyof HitsterCategory, value: any) => {
    const updated = [...customBingoCategories];
    (updated[index] as any)[field] = value;
    setCustomBingoCategories(updated);
  };

  const deleteCustomCategory = (index: number) => {
    const updated = customBingoCategories.filter((_, i) => i !== index);
    setCustomBingoCategories(updated);
  };

  const uploadCustomCategoryIcon = (index: number, file: File | null) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Kies een afbeeldingbestand (png, jpg, webp, svg).');
      return;
    }

    const maxFileSizeBytes = 2 * 1024 * 1024;
    if (file.size > maxFileSizeBytes) {
      alert('Afbeelding is te groot. Kies een bestand kleiner dan 2 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        updateCustomCategory(index, 'icon', result);
      }
    };
    reader.onerror = () => {
      alert('Uploaden van icoon mislukt. Probeer een ander bestand.');
    };
    reader.readAsDataURL(file);
  };

  const handlePrint = () => {
    window.print();
  };

  const pushDiscoballToDisplay = async (options?: {
    silent?: boolean;
    beamAngleOverride?: number;
    selectedCategoryId?: string | null;
    spinning?: boolean;
    activeSongIdOverride?: string | null;
    isPlayingOverride?: boolean;
  }) => {
    if (!event) return;

    const silent = options?.silent ?? false;
    const angleToPush = options?.beamAngleOverride ?? beamAngle;
    const selectedCategoryIdToPush = options?.selectedCategoryId ?? activeCategoryId;
    const spinning = options?.spinning ?? isSpinningDiscoball;
    const songIdToPush = 'activeSongIdOverride' in (options ?? {}) ? options!.activeSongIdOverride : activeSongId;
    const isPlayingToPush = 'isPlayingOverride' in (options ?? {}) ? options!.isPlayingOverride : false;

    if (!silent) {
      setDisplayPushStatus('sending');
    }

    try {
      const response = await fetch(`${API_BASE}/api/display-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'bingo',
          isPlaying: isPlayingToPush,
          showResults: false,
          showBingoCelebration: false,
          bingoCelebrationTitle: undefined,
          bingoCelebrationSubtitle: undefined,
          isIntermission: false,
          showScoreboard: false,
          currentSong: songIdToPush && round
            ? (() => { const s = round.songs.find(s => s.id === songIdToPush); return s ? { id: s.id, title: s.title, artist: s.artist, year: s.year, audioUrl: s.audioUrl, previewUrl: s.previewUrl, spotifyUri: s.spotifyUri, youtubeVideoId: s.youtubeVideoId, youtubeStartTime: s.youtubeStartTime, youtubeEndTime: s.youtubeEndTime, volumeGain: s.volumeGain } : null; })()
            : null,
          eventName: event.name,
          roundName: round?.name || '',
          roundNumber: (round?.order || 0),
          bingoView: 'hitster-spinner',
          bingoHitsterDifficulty: hitsterDifficulty,
          bingoHitsterCategories: hitsterCategories.map((category) => ({
            id: category.id,
            label: category.label,
            color: category.color,
            icon: category.icon,
            description: category.description
          })),
          bingoSelectedCategoryId: selectedCategoryIdToPush,
          bingoBeamAngle: angleToPush,
          bingoIsSpinning: spinning,
          bingoSpinDurationMs: 2600,
          bingoPlayDurationMs: bingoPlayDurationSeconds * 1000,
          bingoPushNonce: Date.now()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      if (songIdToPush && isPlayingToPush) {
        setPlayedSongIds((prev) => {
          const next = new Set(prev);
          next.add(songIdToPush);
          return next;
        });

        setAnswerKeyEntries((prev) => {
          if (prev.some((entry) => entry.songId === songIdToPush)) {
            return prev;
          }

          return [
            ...prev,
            {
              songId: songIdToPush,
              categoryId: selectedCategoryIdToPush,
              playedAt: Date.now()
            }
          ];
        });
      }

      if (!silent) {
        setDisplayPushStatus('ok');
      }
    } catch (error) {
      console.error('Failed to push discoball to display:', error);
      if (!silent) {
        setDisplayPushStatus('error');
      }
    }
  };

  const pushBingoCelebrationToDisplay = async () => {
    if (!event) {
      return;
    }

    setBingoCelebrationStatus('sending');
    try {
      const response = await fetch(`${API_BASE}/api/display-state/bingo-celebration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventName: event.name,
          roundName: round?.name || '',
          title: 'We hebben een bingo!',
          subtitle: 'Kom naar voren, dan controleert de spelleider je kaart.'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      setBingoCelebrationStatus('ok');
    } catch (error) {
      console.error('Failed to push bingo celebration to display:', error);
      setBingoCelebrationStatus('error');
    }
  };

  const spinDiscoball = () => {
    const categories = getHitsterCategories(hitsterDifficulty, customBingoCategories);
    if (isSpinningDiscoball || categories.length === 0) return;

    const winningIndex = Math.floor(Math.random() * categories.length);
    const segmentAngle = 360 / categories.length;
    const targetAngleNormalized = -90 + (winningIndex * segmentAngle);
    const currentNormalized = ((beamAngle % 360) + 360) % 360;
    const targetNormalized = ((targetAngleNormalized % 360) + 360) % 360;
    const delta = (targetNormalized - currentNormalized + 360) % 360;
    const extraRotations = 360 * (4 + Math.floor(Math.random() * 2));
    const finalAngle = beamAngle + extraRotations + delta;

    setIsSpinningDiscoball(true);
    setBeamAngle(finalAngle);
    setActiveSongId(null);

    // Realtime sync: start rotation on display immediately.
    void pushDiscoballToDisplay({
      silent: true,
      beamAngleOverride: finalAngle,
      selectedCategoryId: activeCategoryId,
      spinning: true,
      activeSongIdOverride: null,
      isPlayingOverride: false
    });

    window.setTimeout(() => {
      setIsSpinningDiscoball(false);
      setActiveCategoryId(categories[winningIndex].id);

      // Realtime sync: publish the selected category when the spin ends.
      void pushDiscoballToDisplay({
        silent: true,
        beamAngleOverride: finalAngle,
        selectedCategoryId: categories[winningIndex].id,
        spinning: false,
        activeSongIdOverride: null,
        isPlayingOverride: false
      });
    }, 2600);
  };

  const selectSong = (song: EventSong) => {
    setActiveSongId(song.id);
    setRevealPushStatus('idle');
    void pushDiscoballToDisplay({ silent: true, activeSongIdOverride: song.id, isPlayingOverride: true });
  };

  const revealAnswerOnDisplay = async () => {
    if (!activeSongId) {
      alert('Selecteer eerst een nummer voordat je het antwoord toont.');
      return;
    }

    setRevealPushStatus('sending');
    try {
      // Ensure display has the latest bingo context + active song before revealing.
      await pushDiscoballToDisplay({
        silent: true,
        activeSongIdOverride: activeSongId,
        isPlayingOverride: false,
        selectedCategoryId: activeCategoryId,
        spinning: false
      });

      const response = await fetch(`${API_BASE}/api/display-state/reveal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      setRevealPushStatus('ok');
    } catch (error) {
      console.error('Failed to reveal answer on display:', error);
      setRevealPushStatus('error');
    }
  };

  const registerBingo = async (teamId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/bingo/score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId: eventId,
          roundId: effectiveRoundId,
          teamId: teamId,
          action: 'increment'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Update local state
      setBingoScores(prev => ({
        ...prev,
        [teamId]: (prev[teamId] || 0) + 1
      }));
    } catch (error) {
      console.error('Failed to register bingo:', error);
      alert('Fout bij registratie bingo');
    }
  };

  const toBaseCategoryId = (categoryId: string | null): string | null => {
    if (!categoryId) return null;
    if (categoryId.startsWith('a-') || categoryId.startsWith('b-')) {
      return categoryId.slice(2);
    }
    return categoryId;
  };

  const getExpectedAnswerForCategory = (song: EventSong, categoryId: string | null): string => {
    const baseCategoryId = toBaseCategoryId(categoryId);

    if (!baseCategoryId) {
      return '-';
    }

    const year = Number(song.year);
    const hasValidYear = Number.isFinite(year);

    switch (baseCategoryId) {
      case 'before-2000':
        if (!hasValidYear) return 'Onbekend (geen jaartal)';
        return year < 2000 ? `Ja (voor 2000, ${year})` : `Nee (vanaf 2000, ${year})`;
      case 'within-2-years':
        if (!hasValidYear) return 'Onbekend (geen jaartal)';
        return `${year} (geldig: ${year - 2} t/m ${year + 2})`;
      case 'within-3-years':
        if (!hasValidYear) return 'Onbekend (geen jaartal)';
        return `${year} (geldig: ${year - 3} t/m ${year + 3})`;
      case 'within-4-years':
        if (!hasValidYear) return 'Onbekend (geen jaartal)';
        return `${year} (geldig: ${year - 4} t/m ${year + 4})`;
      case 'decade': {
        if (!hasValidYear) return 'Onbekend (geen jaartal)';
        const decadeStart = Math.floor(year / 10) * 10;
        const decadeLabel = `jaren ${String(decadeStart).slice(-2)}`;
        return `${decadeLabel} (${decadeStart}-${decadeStart + 9})`;
      }
      case 'exact-year':
        return hasValidYear ? String(year) : 'Onbekend (geen jaartal)';
      case 'song-title':
        return song.title || '-';
      case 'artist-name':
        return song.artist || '-';
      case 'group-or-solo': {
        const artist = song.artist || '';
        const lower = artist.toLowerCase();
        const looksLikeGroup =
          /\bband\b|\borchestra\b|\bkoor\b/.test(lower) ||
          /\s(&|x|vs\.?|feat\.?|ft\.?)\s/.test(lower) ||
          artist.includes(',');
        const guess = looksLikeGroup ? 'Groep (waarschijnlijk)' : 'Solo (waarschijnlijk)';
        return `${guess} - ${artist || 'onbekende artiest'}`;
      }
      default:
        return '-';
    }
  };

  const getAnswerCacheKey = (songId: string, categoryId: string | null): string => `${songId}:${categoryId || 'none'}`;

  const resolveExpectedAnswerWithAi = useCallback(async (song: EventSong, categoryId: string | null): Promise<string> => {
    const fallbackAnswer = getExpectedAnswerForCategory(song, categoryId);
    const cacheKey = getAnswerCacheKey(song.id, categoryId);

    const cachedAnswer = aiExpectedAnswers[cacheKey];
    if (cachedAnswer) {
      return cachedAnswer;
    }

    if (!categoryId || fallbackAnswer === '-') {
      return fallbackAnswer;
    }

    if (aiAnswerRequestsRef.current.has(cacheKey)) {
      return fallbackAnswer;
    }

    aiAnswerRequestsRef.current.add(cacheKey);

    try {
      const response = await fetch(`${API_BASE}/api/hitster/expected-answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categoryId,
          song: {
            id: song.id,
            title: song.title,
            artist: song.artist,
            album: song.album,
            year: song.year
          }
        })
      });

      if (!response.ok) {
        return fallbackAnswer;
      }

      const data = await response.json();
      const resolved = typeof data?.expectedAnswer === 'string' && data.expectedAnswer.trim()
        ? data.expectedAnswer.trim()
        : fallbackAnswer;
      const source = typeof data?.source === 'string' ? data.source : 'rule-fallback';
      const providerUsed = typeof data?.providerUsed === 'string' ? data.providerUsed : null;

      let sourceLabel = 'Regel';
      if (source === 'openai' || source === 'openRouter' || source === 'groq') {
        sourceLabel = `AI (${source})`;
      } else if (source === 'rule-fallback') {
        sourceLabel = providerUsed ? `Fallback (${providerUsed})` : 'Fallback';
      }

      setAiExpectedAnswers((prev) => ({ ...prev, [cacheKey]: resolved }));
      setAiAnswerSources((prev) => ({ ...prev, [cacheKey]: sourceLabel }));
      return resolved;
    } catch (error) {
      console.error('Failed to resolve AI expected answer:', error);
      return fallbackAnswer;
    } finally {
      aiAnswerRequestsRef.current.delete(cacheKey);
    }
  }, [aiExpectedAnswers]);

  useEffect(() => {
    if (bingoMode !== 'hitster' || !round || answerKeyEntries.length === 0) {
      return;
    }

    answerKeyEntries.forEach((entry) => {
      const song = round.songs.find((item) => item.id === entry.songId);
      if (!song) return;
      void resolveExpectedAnswerWithAi(song, entry.categoryId);
    });
  }, [bingoMode, round, answerKeyEntries, resolveExpectedAnswerWithAi]);

  const generateAnswerKey = async () => {
    if (!round || !round.songs) {
      alert('Geen ronde geselecteerd of geen nummers beschikbaar');
      return;
    }

    const playedSongsRaw = await Promise.all(answerKeyEntries
      .map(async (entry, index) => {
        const song = round.songs.find((item) => item.id === entry.songId);
        const category = hitsterCategories.find((item) => item.id === entry.categoryId);

        if (!song) {
          return null;
        }

        const expectedAnswer = await resolveExpectedAnswerWithAi(song, entry.categoryId);

        return {
          beurt: index + 1,
          categorie: category?.label || '',
          verwachtAntwoord: expectedAnswer,
          titel: song.title,
          artiest: song.artist,
          album: song.album || '',
          jaar: song.year,
          duurSeconden: song.duration,
          spotifyId: song.spotifyId || '',
          spotifyUri: song.spotifyUri || '',
          previewUrl: song.previewUrl || '',
          audioUrl: song.audioUrl || '',
          youtubeVideoId: song.youtubeVideoId || ''
        };
      }));

    const playedSongs = playedSongsRaw
      .filter((song): song is NonNullable<typeof song> => Boolean(song));

    if (playedSongs.length === 0) {
      alert('Er zijn nog geen nummers via de display afgespeeld in deze ronde.');
      return;
    }

    const csvHeader = 'Beurt,Categorie,Verwacht antwoord,Titel,Artiest,Album,Jaar,Duur (sec),Spotify ID,Spotify URI,Preview URL,Audio URL,YouTube ID\n';
    const csvRows = playedSongs
      .map((song) => [
        song.beurt,
        song.categorie,
        song.verwachtAntwoord,
        song.titel,
        song.artiest,
        song.album,
        song.jaar,
        song.duurSeconden,
        song.spotifyId,
        song.spotifyUri,
        song.previewUrl,
        song.audioUrl,
        song.youtubeVideoId
      ].map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const csv = csvHeader + csvRows;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `antwoordsleutel-${round.name || `ronde-${round.order}`}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const togglePlayed = (songId: string) => {
    setPlayedSongIds(prev => {
      const next = new Set(prev);
      if (next.has(songId)) {
        next.delete(songId);
      } else {
        next.add(songId);
      }
      return next;
    });
  };

  if (loading) {
    return <div className="music-bingo loading">Laden...</div>;
  }

  if (!eventId) {
    // No event selected - show event selector
    return (
      <div className="music-bingo">
        <div className="bingo-header no-print">
          <div className="header-title-group">
            <button className="back-btn" onClick={() => navigate('/spotify-dashboard')} aria-label="Terug naar dashboard" title="Terug naar dashboard">
              ←
            </button>
            <h1>🎵 Muziekbingo</h1>
          </div>
        </div>
        <div className="bingo-info">
          <p>Selecteer eerst een evenement en ronde in het dashboard.</p>
          <button onClick={() => navigate('/spotify-dashboard')}>
            Ga naar Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Event loaded but no round selected - show round selector
  if (eventId && event && !roundId && getEventType(event) !== 'theme-night') {
    return (
      <div className="music-bingo">
        <div className="bingo-header no-print">
          <div className="header-title-group">
            <button className="back-btn" onClick={() => navigate('/spotify-dashboard')} aria-label="Terug naar dashboard" title="Terug naar dashboard">
              ←
            </button>
            <h1>{pageTitle}</h1>
          </div>
        </div>
        <div className="bingo-controls no-print">
          <div className="round-selector">
            <h3>Selecteer een ronde:</h3>
            {event.rounds && event.rounds.length > 0 ? (
              <div className="round-list">
                {event.rounds.map((r: EventRound) => (
                  <button
                    key={r.id}
                    className="round-select-btn"
                    onClick={() => navigate(`${getRouteForRound(r.id)}${requestedMode === 'hitster' ? '?mode=hitster' : ''}`)}
                  >
                    {r.name} ({r.songs?.length || 0} nummers)
                  </button>
                ))}
              </div>
            ) : (
              <p>Nog geen rondes aangemaakt. Ga naar het dashboard om rondes toe te voegen.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const songs = round?.songs || [];
  const hasEnoughSongs = songs.length >= 24;
  const canGenerateCards = bingoMode === 'hitster' || hasEnoughSongs;
  const hitsterCategories = getHitsterCategories(hitsterDifficulty, customBingoCategories);
  const selectedHitsterCategory = hitsterCategories.find((category) => category.id === activeCategoryId);
  const eventRounds = [...(event?.rounds || [])].sort((left, right) => left.order - right.order);
  const currentRoundIndex = round ? eventRounds.findIndex((eventRound) => eventRound.id === round.id) : -1;
  const previousRound = currentRoundIndex > 0 ? eventRounds[currentRoundIndex - 1] : null;
  const nextRound = currentRoundIndex >= 0 && currentRoundIndex < eventRounds.length - 1
    ? eventRounds[currentRoundIndex + 1]
    : null;

  const navigateToRound = (targetRoundId: string) => {
    if (!eventId || !targetRoundId || targetRoundId === roundId) {
      return;
    }

    navigate(`${getRouteForRound(targetRoundId)}${requestedMode === 'hitster' ? '?mode=hitster' : ''}`);
  };

  return (
    <div className="music-bingo">
      {/* Controls - hidden when printing */}
      <div className={`bingo-controls no-print${bingoMode === 'hitster' ? ' hitster-wide' : ''}`}>
        <div className="controls-header">
          <div className="header-title-group">
            <button className="back-btn" onClick={() => navigate('/spotify-dashboard')} aria-label="Terug naar dashboard" title="Terug naar dashboard">
              ←
            </button>
            <h1>{pageTitle}</h1>
          </div>
          {round && (
            <button 
              className="settings-icon-btn"
              onClick={() => setShowSettingsModal((prev) => !prev)}
              title="Instellingen"
            >
              ⚙️
            </button>
          )}

          {showSettingsModal && (
            <div className="settings-dropdown-panel">
              <h3>Instellingen</h3>

              {round && (
                <div className="settings-round-summary">
                  <div><strong>Ronde:</strong> {round.name}</div>
                  <div><strong>Beschikbaar:</strong> {songs.length} nummers</div>
                  <div><strong>Gespeeld:</strong> {playedSongIds.size} van {songs.length}</div>
                </div>
              )}

              <div className="setting-row">
                <label>Spelvariant:</label>
                <select value={bingoMode} onChange={(e) => setBingoMode(e.target.value as BingoMode)}>
                  <option value="music">Muziekbingo (titels/artiesten)</option>
                  <option value="hitster">Hitster Bingo (officiele categorieen)</option>
                </select>
              </div>

              {bingoMode === 'hitster' && (
                <>
                  <div className="setting-row">
                    <label>Moeilijkheid:</label>
                    <select
                      value={hitsterDifficulty}
                      onChange={(e) => setHitsterDifficulty(e.target.value as HitsterDifficulty)}
                    >
                      <option value="beginner">Kant A - Beginners</option>
                      <option value="expert">Kant B - Experts</option>
                      <option value="mixed">Mix - Kant A + B</option>
                      <option value="empty">Lege variant (eigen categorieën)</option>
                    </select>
                  </div>

                  {hitsterDifficulty === 'empty' && (
                    <div className="setting-row">
                      <button
                        type="button"
                        onClick={() => setShowCategoryEditor(true)}
                        className="edit-categories-btn"
                        style={{
                          backgroundColor: '#1DB954',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 14px',
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: '0.9rem'
                        }}
                      >
                        ✏️ Categorieën bewerken ({customBingoCategories.length}/10)
                      </button>
                    </div>
                  )}

                  <div className="hitster-rules">
                    <strong>Hitster Bingo regels:</strong> markeer per beurt het vak van de categorie als je antwoord klopt. Win met een volledige lijn (horizontaal, verticaal of diagonaal).
                  </div>
                </>
              )}

              <div className="setting-row">
                <label>Aantal kaartjes:</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={numCards}
                  onChange={(e) => setNumCards(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                />
              </div>

              <div className="setting-row">
                <label>Kaartjes per pagina:</label>
                <select value={cardsPerPage} onChange={(e) => setCardsPerPage(parseInt(e.target.value))}>
                  <option value={1}>1 kaartje</option>
                  <option value={2}>2 kaartjes</option>
                  <option value={4}>4 kaartjes</option>
                </select>
              </div>

              <div className="setting-row checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={useFreeSpace}
                    onChange={(e) => setUseFreeSpace(e.target.checked)}
                  />
                  Gratis vakje in midden
                </label>
              </div>

              {bingoMode === 'hitster' && (
                <div className="setting-row">
                  <label>⏱️ Speelduur per nummer (sec):</label>
                  <input
                    type="number"
                    value={bingoPlayDurationSeconds}
                    onChange={(e) => setBingoPlayDurationSeconds(Math.max(1, parseInt(e.target.value) || 25))}
                    min="1"
                    max="120"
                    style={{ width: '80px' }}
                  />
                  <small style={{ display: 'block', marginTop: '4px', color: '#999' }}>Hoe lang ieder nummer speelt voordat hints verdwijnen</small>
                </div>
              )}

              {bingoMode === 'music' && (
                <>
                  <div className="setting-row checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={showTitle}
                        onChange={(e) => setShowTitle(e.target.checked)}
                      />
                      Toon titel
                    </label>
                  </div>

                  <div className="setting-row checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={showArtist}
                        onChange={(e) => setShowArtist(e.target.checked)}
                      />
                      Toon artiest
                    </label>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Category Editor Modal */}
        {showCategoryEditor && hitsterDifficulty === 'empty' && (
          <div className="modal-overlay" style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="modal-content" style={{ backgroundColor: '#1e1e2e', borderRadius: '12px', width: 'min(700px, 95vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #333' }}>
                <h2 style={{ margin: 0, fontSize: '1.2rem', color: '#fff' }}>Categorieën Bewerken</h2>
                <button
                  onClick={() => {
                    setShowCategoryEditor(false);
                    saveCustomCategories();
                  }}
                  style={{ background: 'none', border: 'none', color: '#aaa', fontSize: '1.2rem', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px' }}
                  onMouseOver={(e) => { (e.target as HTMLElement).style.background = '#333'; (e.target as HTMLElement).style.color = '#fff'; }}
                  onMouseOut={(e) => { (e.target as HTMLElement).style.background = 'none'; (e.target as HTMLElement).style.color = '#aaa'; }}
                >
                  ✕
                </button>
              </div>
              
              <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
                <p style={{ color: '#aaa', marginBottom: '12px' }}>Je kunt maximaal 10 categorieën toevoegen. Elke categorie verschijnt in het wiel met een aangepaste kleur en icon.</p>
                
                {customBingoCategories.map((category, index) => (
                  <div key={category.id} style={{ marginBottom: '14px', padding: '12px', backgroundColor: '#2a2a3e', borderRadius: '8px', border: `2px solid ${category.color}33` }}>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <input
                        type="text"
                        placeholder="Categorie naam"
                        value={category.label}
                        onChange={(e) => updateCustomCategory(index, 'label', e.target.value)}
                        style={{ flex: 1, padding: '8px', backgroundColor: '#1e1e2e', color: '#fff', border: '1px solid #444', borderRadius: '4px' }}
                      />
                      <button
                        onClick={() => deleteCustomCategory(index)}
                        style={{ padding: '8px 12px', backgroundColor: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        🗑️
                      </button>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <input
                        type="text"
                        placeholder="Beschrijving"
                        value={category.description}
                        onChange={(e) => updateCustomCategory(index, 'description', e.target.value)}
                        style={{ flex: 1, padding: '8px', backgroundColor: '#1e1e2e', color: '#fff', border: '1px solid #444', borderRadius: '4px', fontSize: '0.9rem' }}
                      />
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        value={CUSTOM_COLORS.indexOf(category.color)}
                        onChange={(e) => updateCustomCategory(index, 'color', CUSTOM_COLORS[parseInt(e.target.value)])}
                        style={{ padding: '6px', backgroundColor: '#1e1e2e', color: '#fff', border: '1px solid #444', borderRadius: '4px' }}
                      >
                        <option value="0" style={{ backgroundColor: '#9adf7f', color: '#000' }}>🟢 Groen</option>
                        <option value="1" style={{ backgroundColor: '#d8b5ff', color: '#000' }}>🟣 Paars</option>
                        <option value="2" style={{ backgroundColor: '#ffe68a', color: '#000' }}>🟡 Geel</option>
                        <option value="3" style={{ backgroundColor: '#ff8fd6', color: '#000' }}>🔴 Roze</option>
                        <option value="4" style={{ backgroundColor: '#8ad9ff', color: '#000' }}>🔵 Cyaan</option>
                      </select>
                      
                      <input
                        type="text"
                        placeholder="Icon (emoji, /pad/icoon.webp of https://...)"
                        value={category.icon}
                        onChange={(e) => updateCustomCategory(index, 'icon', e.target.value)}
                        style={{ flex: 1, padding: '6px', backgroundColor: '#1e1e2e', color: '#fff', border: '1px solid #444', borderRadius: '4px' }}
                      />

                      <div
                        style={{
                          width: '34px',
                          height: '34px',
                          borderRadius: '6px',
                          border: '1px solid #555',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#151520',
                          overflow: 'hidden',
                          flexShrink: 0
                        }}
                        title="Icon preview"
                      >
                        {isImageIcon(category.icon) ? (
                          <img
                            src={category.icon}
                            alt={category.label || 'Categorie icon'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <span style={{ fontSize: '1rem', lineHeight: 1 }}>{category.icon || '🎵'}</span>
                        )}
                      </div>
                    </div>
                    <small style={{ display: 'block', marginTop: '6px', color: '#999' }}>
                      Tip: gebruik emoji/URL of upload direct een afbeeldingsbestand.
                    </small>
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <label
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '6px 10px',
                          backgroundColor: '#2d6cdf',
                          color: '#fff',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: 600
                        }}
                      >
                        🖼 Upload icoon
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            uploadCustomCategoryIcon(index, e.target.files?.[0] || null);
                            e.currentTarget.value = '';
                          }}
                        />
                      </label>

                      <button
                        type="button"
                        onClick={() => updateCustomCategory(index, 'icon', '🎵')}
                        style={{
                          padding: '6px 10px',
                          border: '1px solid #555',
                          backgroundColor: '#1e1e2e',
                          color: '#ddd',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Reset icoon
                      </button>
                    </div>
                  </div>
                ))}
                
                {customBingoCategories.length < 10 && (
                  <button
                    onClick={addCustomCategory}
                    style={{
                      width: '100%',
                      padding: '10px',
                      marginTop: '12px',
                      backgroundColor: '#1DB954',
                      color: '#000',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    + Categorie toevoegen ({customBingoCategories.length}/10)
                  </button>
                )}
              </div>
              
              <div style={{ padding: '12px 20px', borderTop: '1px solid #333', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowCategoryEditor(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Annuleren
                </button>
                <button
                  onClick={() => {
                    setShowCategoryEditor(false);
                    saveCustomCategories();
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#1DB954',
                    color: '#000',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  ✓ Opslaan
                </button>
              </div>
            </div>
          </div>
        )}

        {bingoMode === 'hitster' && (
          <>
            <div className="hitster-view-toggle">
              <button 
                className={adminViewLayout === 'stacked' ? 'active' : ''} 
                onClick={() => setAdminViewLayout('stacked')}
              >
                📋 Gestapeld
              </button>
              <button 
                className={adminViewLayout === 'side-by-side' ? 'active' : ''} 
                onClick={() => setAdminViewLayout('side-by-side')}
              >
                ⟷ Naast elkaar
              </button>
            </div>

            <div className={`hitster-controls-layout${adminViewLayout === 'side-by-side' ? ' side-by-side' : ''}`}>
              <div className="hitster-spinner-panel">
                <h3>Stap 1: Draai de discobal</h3>
                <p>
                  Draai de discobal. De lichtstraal bepaalt met welke categorie je deze beurt speelt.
                </p>

                <div className="hitster-spinner">
                  <div
                    className="hitster-beam"
                    style={{ transform: `rotate(${beamAngle}deg)` }}
                    aria-hidden="true"
                />

                  {hitsterCategories.map((category, index) => {
                    const categoryCount = hitsterCategories.length;
                    const previousCategory = hitsterCategories[(index - 1 + categoryCount) % categoryCount];
                    const boundaryAngle = -90 - (180 / categoryCount) + index * (360 / categoryCount);

                    return (
                      <div
                        key={`boundary-${category.id}`}
                        className="hitster-boundary-pair"
                        style={{ transform: `rotate(${boundaryAngle}deg)` }}
                      >
                        <span className="hitster-boundary-line" style={{ backgroundColor: previousCategory.color }} />
                        <span className="hitster-boundary-line" style={{ backgroundColor: category.color }} />
                      </div>
                    );
                  })}

                  {hitsterCategories.map((category, index) => {
                    const categoryCount = hitsterCategories.length;
                    
                    // For 10 categories (2 per color), we need to position them within their color section
                    const isDoubleCategoryMode = categoryCount === 10;
                    
                    let angle: number;
                    let positionOffset: number = 0; // For offsetting within the section
                    
                    if (isDoubleCategoryMode) {
                      // Group by color (2 categories per color section)
                      const colorGroup = Math.floor(index / 2);
                      const itemInGroup = index % 2;
                      const sectionAngle = 360 / 5; // 5 colors
                      
                      // Center angle for this color section
                      const centerAngle = -90 + colorGroup * sectionAngle;
                      
                      // Offset within the section (first item -9 degrees, second item +9 degrees)
                      const inSectionOffset = (itemInGroup === 0 ? -9 : 9);
                      angle = centerAngle + inSectionOffset;
                      positionOffset = itemInGroup === 0 ? -15 : 15; // Offset distance
                    } else {
                      angle = -90 + index * (360 / categoryCount);
                    }
                    
                    const radians = (angle * Math.PI) / 180;
                    const baseSquareRadius = 152;
                    const squareRadius = baseSquareRadius + positionOffset;
                    const symbolRadius = squareRadius;
                    const squareX = Math.cos(radians) * squareRadius;
                    const squareY = Math.sin(radians) * squareRadius;
                    const symbolX = Math.cos(radians) * symbolRadius;
                    const symbolY = Math.sin(radians) * symbolRadius;
                    const isActive = category.id === activeCategoryId;

                    return (
                      <React.Fragment key={category.id}>
                        <div
                          className={`hitster-category-tile ${isActive ? 'active' : ''}`}
                          style={{
                            left: `calc(50% + ${squareX}px)`,
                            top: `calc(50% + ${squareY}px)`,
                            borderColor: category.color,
                            boxShadow: `0 0 0 1px ${category.color}66, 0 0 18px ${category.color}55`,
                            width: isDoubleCategoryMode ? '48px' : '56px',
                            height: isDoubleCategoryMode ? '48px' : '56px'
                          }}
                        >
                          <div className="hitster-category-square" style={{ backgroundColor: category.color }} />
                        </div>

                        <div
                          className={`hitster-symbol-marker ${isActive ? 'active' : ''}`}
                          style={{
                            left: `calc(50% + ${symbolX}px)`,
                            top: `calc(50% + ${symbolY}px)`,
                            color: category.color
                          }}
                        >
                          {isImageIcon(category.icon) ? (
                            <img className="hitster-symbol-icon-image" src={category.icon} alt={category.label} />
                          ) : (
                            <span className="hitster-symbol-icon-text">{category.icon}</span>
                          )}
                          {(hitsterDifficulty === 'expert' || hitsterDifficulty === 'empty') && (
                            <span className="hitster-symbol-label" style={{ fontSize: isDoubleCategoryMode ? '0.75rem' : '0.85rem' }}>{category.label}</span>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}

                  <button
                    type="button"
                    className={`discoball-button ${isSpinningDiscoball ? 'spinning' : ''}`}
                    onClick={spinDiscoball}
                    disabled={isSpinningDiscoball}
                    title="Draai de discobal"
                  >
                    <span className="discoball-icon">🪩</span>
                  </button>
                </div>

              <div className="hitster-spin-result">
                {selectedHitsterCategory ? (
                  <>
                    <strong>Geselecteerde categorie:</strong> {selectedHitsterCategory.label}
                  </>
                ) : (
                  <>Klik op de discobal om een categorie te kiezen.</>
                )}
              </div>

              <div className="hitster-display-actions">
                <button
                  type="button"
                  className="push-display-btn"
                  onClick={() => pushDiscoballToDisplay({ silent: false })}
                  disabled={displayPushStatus === 'sending'}
                >
                  {displayPushStatus === 'sending' ? '📡 Pushen...' : '📺 Toon op display'}
                </button>

                <button
                  type="button"
                  className="push-display-btn reveal-answer-btn"
                  onClick={revealAnswerOnDisplay}
                  disabled={revealPushStatus === 'sending' || !activeSongId}
                  title={!activeSongId ? 'Kies eerst een nummer' : 'Toon het antwoord op de display'}
                >
                  {revealPushStatus === 'sending' ? '⏳ Tonen...' : '✅ Toon antwoord'}
                </button>

                <button
                  type="button"
                  className="push-display-btn bingo-celebration-btn"
                  onClick={pushBingoCelebrationToDisplay}
                  disabled={bingoCelebrationStatus === 'sending'}
                  title="Toon een feestscherm op de display als iemand bingo heeft"
                >
                  {bingoCelebrationStatus === 'sending' ? '🎉 Pushen...' : '🎊 We hebben een bingo!'}
                </button>

                {displayPushStatus === 'ok' && (
                  <span className="push-status ok">Discobal naar display gestuurd</span>
                )}
                {displayPushStatus === 'error' && (
                  <span className="push-status error">Push mislukt, probeer opnieuw</span>
                )}
                {revealPushStatus === 'ok' && (
                  <span className="push-status ok">Antwoord op display getoond</span>
                )}
                {revealPushStatus === 'error' && (
                  <span className="push-status error">Antwoord tonen mislukt</span>
                )}
                {bingoCelebrationStatus === 'ok' && (
                  <span className="push-status ok">Bingo-feestscherm op display getoond</span>
                )}
                {bingoCelebrationStatus === 'error' && (
                  <span className="push-status error">Bingo-feestscherm tonen mislukt</span>
                )}
              </div>
            </div>

            <div className="hitster-categories">
              <h3>
                Categorieen ({hitsterDifficulty === 'beginner'
                  ? 'Kant A'
                  : hitsterDifficulty === 'expert'
                    ? 'Kant B'
                    : 'Mix A + B'})
              </h3>
              <ul>
                {hitsterCategories.map((category) => (
                  <li key={category.id}>
                    {isImageIcon(category.icon) ? (
                      <img className="category-icon-image" src={category.icon} alt={category.label} />
                    ) : (
                      <span className="category-icon">{category.icon}</span>
                    )}
                    <span className="category-label">{category.label}</span>
                    <span className="category-desc">{category.description}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Stap 2: Playlist panel */}
            {songs.length > 0 && (
              <div className="hitster-playlist-panel">
                <h3>Stap 2: Kies een nummer</h3>
                <div className="hitster-playlist-meta">
                  <span>{playedSongIds.size} van {songs.length} nummers gespeeld</span>
                  {round && (
                    <div className="hitster-playlist-round-nav">
                      <button
                        type="button"
                        className="hitster-round-nav-btn"
                        onClick={() => previousRound && navigateToRound(previousRound.id)}
                        disabled={!previousRound}
                        title={previousRound ? `Ga naar ${previousRound.name}` : 'Geen vorige ronde'}
                      >
                        ←
                      </button>
                      <span className="hitster-playlist-round-badge">
                        {round.name || `Ronde ${round.order || ''}`}
                      </span>
                      <button
                        type="button"
                        className="hitster-round-nav-btn"
                        onClick={() => nextRound && navigateToRound(nextRound.id)}
                        disabled={!nextRound}
                        title={nextRound ? `Ga naar ${nextRound.name}` : 'Geen volgende ronde'}
                      >
                        →
                      </button>
                      <select
                        className="hitster-round-nav-select"
                        value={round.id}
                        onChange={(event) => navigateToRound(event.target.value)}
                        aria-label="Kies een andere ronde"
                      >
                        {eventRounds.map((eventRound) => (
                          <option key={eventRound.id} value={eventRound.id}>
                            {eventRound.name || `Ronde ${eventRound.order || ''}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {activeSongId && selectedHitsterCategory && (
                    <span
                      className="hitster-playlist-active-badge"
                      style={{ backgroundColor: selectedHitsterCategory.color }}
                    >
                      {selectedHitsterCategory.label}
                    </span>
                  )}
                </div>
                <ul className="hitster-playlist">
                  {songs.map((song, index) => {
                    const isActive = song.id === activeSongId;
                    const isPlayed = playedSongIds.has(song.id);
                    return (
                      <li
                        key={song.id}
                        className={`hitster-playlist-row${isActive ? ' active' : ''}${isPlayed ? ' played' : ''}`}
                        style={isActive && selectedHitsterCategory ? { borderColor: selectedHitsterCategory.color } : undefined}
                        onClick={() => selectSong(song)}
                      >
                        <span className="hitster-playlist-index">{index + 1}</span>
                        <span className="hitster-playlist-title">
                          {isPlayed && <span className="hitster-played-check">✓ </span>}
                          {song.artist} – {song.title}
                        </span>
                        <button
                          className={`hitster-played-btn${isPlayed ? ' is-played' : ''}`}
                          title={isPlayed ? 'Markeer als niet gespeeld' : 'Markeer als gespeeld'}
                          onClick={(e) => { e.stopPropagation(); togglePlayed(song.id); }}
                        >
                          {isPlayed ? '✓' : '○'}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            </div>
          </>
        )}

        {/* Bingo Scoreboard */}
        {bingoMode === 'hitster' && event && (
          <div className="settings-panel bingo-scoreboard" id="bingo-scoreboard-panel">
            <h3>🏆 Bingo Scorebord</h3>
            {event.teams && event.teams.length > 0 ? (
              <div className="bingo-scores-grid">
                {event.teams.map((team) => (
                  <div key={team.id} className="bingo-score-card">
                    <div className="team-name">{team.name}</div>
                    <div className="team-score">{bingoScores[team.id] || 0}</div>
                    <button
                      className="bingo-increment-btn"
                      onClick={() => registerBingo(team.id)}
                      title={`Registreer bingo voor ${team.name}`}
                    >
                      +1 BINGO
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-teams-message">Geen teams gevonden. Voeg teams toe in het evenement.</p>
            )}
          </div>
        )}

        {bingoMode === 'hitster' && round && (
          <div className="settings-panel answer-key-panel" id="answer-key-panel">
            <div className="answer-key-header-row">
              <div>
                <h3>📋 Antwoordsleutel Spelleider</h3>
                <p>
                  Deze lijst vult zichzelf met elk nummer dat echt via de display is afgespeeld in deze ronde.
                </p>
              </div>
              <button
                type="button"
                className="push-display-btn answer-key-export-btn"
                onClick={() => { void generateAnswerKey(); }}
                disabled={answerKeyEntries.length === 0}
                title={answerKeyEntries.length === 0 ? 'Speel eerst een nummer via de display' : 'Download CSV voor controle'}
              >
                ⬇️ Download antwoordsleutel
              </button>
            </div>

            {answerKeyEntries.length > 0 ? (
              <div className="answer-key-table-wrap">
                <table className="answer-key-table">
                  <thead>
                    <tr>
                      <th>Beurt</th>
                      <th>Categorie</th>
                      <th>Verwacht antwoord</th>
                      <th>Titel</th>
                      <th>Artiest</th>
                      <th>Album</th>
                      <th>Jaar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {answerKeyEntries.map((entry, index) => {
                      const song = round.songs.find((item) => item.id === entry.songId);
                      const category = hitsterCategories.find((item) => item.id === entry.categoryId);

                      if (!song) {
                        return null;
                      }

                      const cacheKey = getAnswerCacheKey(song.id, entry.categoryId);
                      const expectedAnswer = aiExpectedAnswers[cacheKey] || getExpectedAnswerForCategory(song, entry.categoryId);
                      const sourceLabel = aiAnswerSources[cacheKey] || 'Regel';
                      const sourceClass = sourceLabel.startsWith('AI')
                        ? 'ai'
                        : sourceLabel.startsWith('Fallback')
                          ? 'fallback'
                          : 'rule';

                      return (
                        <tr key={entry.songId}>
                          <td>{index + 1}</td>
                          <td>
                            {category ? (
                              <span className="answer-key-category-chip">
                                <span
                                  className="answer-key-category-dot"
                                  style={{ backgroundColor: category.color }}
                                  aria-hidden="true"
                                />
                                <span>{category.label}</span>
                              </span>
                            ) : '-'}
                          </td>
                          <td>
                            <div className="answer-key-expected-cell">
                              <span>{expectedAnswer}</span>
                              <span className={`answer-key-source-tag ${sourceClass}`}>{sourceLabel}</span>
                            </div>
                          </td>
                          <td>{song.title}</td>
                          <td>{song.artist}</td>
                          <td>{song.album || '-'}</td>
                          <td>{song.year}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-teams-message">Nog geen nummers naar de display gestuurd in deze ronde.</p>
            )}
          </div>
        )}

        {/* Physical Card Generation Controls */}
        {bingoMode === 'hitster' && (
          <div className="settings-panel">
            <h3>🎴 Fysieke Kaarten Generator</h3>
            
            <div className="setting-row">
              <label>Titel op kaart:</label>
              <input
                type="text"
                value={cardTitle}
                onChange={(e) => setCardTitle(e.target.value.toUpperCase().slice(0, 14))}
                placeholder="HITSTER"
                maxLength={14}
              />
            </div>

            <div className="setting-row">
              <label>Wit vlak vullen met:</label>
              <select value={cardWriteMode} onChange={(e) => setCardWriteMode(e.target.value as any)}>
                <option value="blank">Leeg laten</option>
                <option value="numbers">Kaartnummer</option>
                <option value="names">Namen uit lijst</option>
              </select>
            </div>

            {cardWriteMode === 'names' && (
              <div className="setting-row">
                <label>Namen/teksten (één per regel):</label>
                <textarea
                  value={cardNames}
                  onChange={(e) => setCardNames(e.target.value)}
                  placeholder="Voer namen of teksten in..."
                  style={{ height: '80px', fontFamily: 'monospace', fontSize: '12px' }}
                />
              </div>
            )}
          </div>
        )}

        <div className="action-buttons">
          <button 
            className="generate-btn"
            onClick={generateCards}
            disabled={!canGenerateCards}
          >
            🎲 Genereer {numCards} kaartjes
          </button>
          
          {cards.length > 0 && (
            <>
              <button 
                className="print-btn" 
                onClick={() => setViewPhysicalCards(!viewPhysicalCards)}
              >
                {viewPhysicalCards ? '📋 Tabel View' : '🎴 Fysieke Kaarten'}
              </button>
              <button className="print-btn" onClick={handlePrint}>
                🖨️ Printen
              </button>
            </>
          )}
        </div>

        {bingoMode === 'music' && !hasEnoughSongs && (
          <div className="warning">
            ⚠️ Je hebt minimaal 24 nummers nodig. Voeg meer nummers toe aan de ronde.
          </div>
        )}
      </div>

      {/* Bingo cards - visible when printing */}
      {cards.length > 0 && !viewPhysicalCards && bingoMode !== 'hitster' && (
        <div className={`bingo-cards-container cards-per-page-${cardsPerPage}`}>
          {cards.map((card, cardIndex) => (
            <div key={card.id} className="bingo-card">
              <div className="card-header">
                <span className="card-title">🎵 MUZIEKBINGO</span>
                <span className="card-number">#{cardIndex + 1}</span>
              </div>
              
              <table className="bingo-grid">
                <thead>
                  <tr>
                    <th>B</th>
                    <th>I</th>
                    <th>N</th>
                    <th>G</th>
                    <th>O</th>
                  </tr>
                </thead>
                <tbody>
                  {card.cells.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, colIndex) => (
                        <td key={colIndex} className={cell === null ? 'free-space' : ''}>
                          {cell === null ? (
                            <div className="free-space-content">
                              <span className="star">⭐</span>
                              <span className="free-text">GRATIS</span>
                            </div>
                          ) : (
                            <>
                              {'categoryId' in cell ? (
                                <div className="hitster-cell">
                                  <span className="hitster-cell-title">{cell.label}</span>
                                  <span className="hitster-cell-desc">{cell.description}</span>
                                </div>
                              ) : (
                                <div className="song-cell">
                                  {showTitle && <span className="song-title">{cell.title}</span>}
                                  {showArtist && <span className="song-artist">{cell.artist}</span>}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              
              <div className="card-footer">
                {event?.name} • {round?.name}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Physical bingo cards */}
      {cards.length > 0 && viewPhysicalCards && bingoMode === 'hitster' && (
        <div className="bingo-cards-physical-container per-page-2">
          {Array.from({ length: numCards }).map((_, cardIndex) => {
            const pattern = makeGridPattern(cardIndex);
            const names = cardNames.split('\n').map(x => x.trim()).filter(x => x);
            const writeText = getWriteText(cardIndex, cardWriteMode, names);
            
            return (
              <PhysicalBingoCard
                key={cardIndex}
                index={cardIndex}
                pattern={pattern}
                title={cardTitle || 'HITSTER'}
                writeText={writeText}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MusicBingo;
